
import React, { useState } from 'react';
import { FilterOptions } from '@/context/AttachmentContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Filter, X, ChevronDown, Calendar, FileType, Download } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';

interface AttachmentFiltersProps {
  filters: FilterOptions;
  onUpdateFilters: (newFilters: Partial<FilterOptions>) => void;
  onSortChange: (field: 'emailDate' | 'fileSize' | 'fileName', direction: 'asc' | 'desc') => void;
  currentSortField: string;
  currentSortDirection: string;
  fileTypes: string[];
  hasActiveFilters: boolean;
  resetFilters: () => void;
}

const AttachmentFilters: React.FC<AttachmentFiltersProps> = ({
  filters,
  onUpdateFilters,
  onSortChange,
  currentSortField,
  currentSortDirection,
  fileTypes,
  hasActiveFilters,
  resetFilters,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(filters.dateRange.start);
  const [endDate, setEndDate] = useState<Date | null>(filters.dateRange.end);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(filters.fileType);
  const [sizeRange, setSizeRange] = useState<[number]>([
    filters.sizeRange.max ? Math.log10(filters.sizeRange.max) / 7 * 100 : 100
  ]);
  
  // Date range handler
  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
    onUpdateFilters({
      dateRange: {
        start,
        end,
      },
    });
  };
  
  // File type handler
  const handleFileTypeChange = (type: string) => {
    const updated = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    
    setSelectedTypes(updated);
    onUpdateFilters({
      fileType: updated,
    });
  };
  
  // Size range handler
  const handleSizeRangeChange = (value: number[]) => {
    // Convert slider value (0-100) to file size in bytes
    // Using logarithmic scale for better UX
    const sliderValue = value[0];
    setSizeRange([sliderValue]);
    
    if (sliderValue >= 99) {
      // No upper limit
      onUpdateFilters({
        sizeRange: {
          min: null,
          max: null,
        },
      });
    } else {
      const maxBytes = Math.pow(10, sliderValue / 100 * 7); // Up to 10MB
      onUpdateFilters({
        sizeRange: {
          min: null,
          max: maxBytes,
        },
      });
    }
  };
  
  // Helper for displaying file size
  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return 'Any size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  return (
    <div className="mb-6 space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by filename or subject..."
            className="pl-10 w-full"
            value={filters.search}
            onChange={(e) => onUpdateFilters({ search: e.target.value })}
          />
          {filters.search && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              onClick={() => onUpdateFilters({ search: '' })}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        
        {/* Sort Dropdown */}
        <div className="flex-shrink-0">
          <Select
            value={`${currentSortField}-${currentSortDirection}`}
            onValueChange={(value) => {
              const [field, direction] = value.split('-');
              onSortChange(
                field as 'emailDate' | 'fileSize' | 'fileName',
                direction as 'asc' | 'desc'
              );
            }}
          >
            <SelectTrigger className="min-w-[180px]">
              <div className="flex items-center">
                <Download className={`mr-2 h-3.5 w-3.5 ${currentSortDirection === 'desc' ? 'transform rotate-180' : ''}`} />
                <SelectValue placeholder="Sort by" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="emailDate-desc">Newest first</SelectItem>
              <SelectItem value="emailDate-asc">Oldest first</SelectItem>
              <SelectItem value="fileSize-desc">Largest first</SelectItem>
              <SelectItem value="fileSize-asc">Smallest first</SelectItem>
              <SelectItem value="fileName-asc">Name (A-Z)</SelectItem>
              <SelectItem value="fileName-desc">Name (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Filters Button */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-shrink-0 relative">
              <Filter className="mr-2 h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Filters</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={resetFilters}
                  disabled={!hasActiveFilters}
                >
                  Reset all
                </Button>
              </div>
              
              <Separator />
              
              {/* Date Range Filter */}
              <div>
                <h5 className="text-sm font-medium mb-2 flex items-center">
                  <Calendar className="mr-2 h-4 w-4" />
                  Date Range
                </h5>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left text-xs h-8 font-normal"
                        >
                          {startDate ? format(startDate, 'MMM d, yyyy') : 'Any time'}
                          <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={startDate || undefined}
                          onSelect={setStartDate}
                          initialFocus
                          onDayClick={(day) => {
                            handleDateRangeChange(day, endDate);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">End Date</p>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left text-xs h-8 font-normal"
                        >
                          {endDate ? format(endDate, 'MMM d, yyyy') : 'Present'}
                          <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={endDate || undefined}
                          onSelect={setEndDate}
                          initialFocus
                          onDayClick={(day) => {
                            handleDateRangeChange(startDate, day);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                {(startDate || endDate) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-1 text-xs h-6"
                    onClick={() => handleDateRangeChange(null, null)}
                  >
                    Clear dates
                  </Button>
                )}
              </div>
              
              <Separator />
              
              {/* File Type Filter */}
              <div>
                <h5 className="text-sm font-medium mb-2 flex items-center">
                  <FileType className="mr-2 h-4 w-4" />
                  File Type
                </h5>
                <div className="grid grid-cols-2 gap-2">
                  {fileTypes.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${type}`}
                        checked={selectedTypes.includes(type)}
                        onCheckedChange={() => handleFileTypeChange(type)}
                      />
                      <label
                        htmlFor={`type-${type}`}
                        className="text-sm cursor-pointer"
                      >
                        {type.toUpperCase()}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              {/* Size Range Filter */}
              <div>
                <h5 className="text-sm font-medium mb-2">File Size</h5>
                <Slider
                  value={sizeRange}
                  max={100}
                  step={1}
                  onValueChange={handleSizeRangeChange}
                  className="my-4"
                />
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>0</span>
                  <span className="font-medium text-foreground">
                    {sizeRange[0] >= 99 
                      ? 'Any size' 
                      : `Up to ${formatFileSize(Math.pow(10, sizeRange[0] / 100 * 7))}`}
                  </span>
                  <span>10MB+</span>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      
      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 mt-2">
          {filters.dateRange.start && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>From: {format(filters.dateRange.start, 'MMM d, yyyy')}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => handleDateRangeChange(null, filters.dateRange.end)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          
          {filters.dateRange.end && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>To: {format(filters.dateRange.end, 'MMM d, yyyy')}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => handleDateRangeChange(filters.dateRange.start, null)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          
          {filters.fileType.map((type) => (
            <Badge key={type} variant="secondary" className="flex items-center gap-1">
              <span>{type.toUpperCase()}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => handleFileTypeChange(type)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
          
          {filters.sizeRange.max && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>Up to {formatFileSize(filters.sizeRange.max)}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => handleSizeRangeChange([100])}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentFilters;
